"use client";

import { useState } from "react";

import { TruckIcon } from "lucide-react";
import { useFormatter, useTranslations } from "next-intl";

import { PrimDialog } from "@/shared-web.ui/prim.dialog";
import { PrimPopover } from "@/shared-web.ui/prim.popover";
import { Text } from "@/shared-web.ui/ui.text";
import { cn } from "@/shared-web.ui/util.cn";

import { type ServerDataOutput } from "../data.subscription-data";
import { getDaysUntil } from "./util.days-until";

type CalendarEventType = "cutoff" | "holiday" | "shipping";

const DAYS_PER_WEEK = 7;

const getCalendarWeeks = (year: number, month: number): (Date | null)[][] => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = lastDay.getDate();
  const numWeeks = Math.ceil((startOffset + daysInMonth) / DAYS_PER_WEEK);
  const weeks: (Date | null)[][] = [];

  for (let week = 0; week < numWeeks; week += 1) {
    const row: (Date | null)[] = [];
    for (let col = 0; col < DAYS_PER_WEEK; col += 1) {
      const dayNumber = week * DAYS_PER_WEEK + col - startOffset + 1;
      if (dayNumber >= 1 && dayNumber <= daysInMonth) {
        row.push(new Date(year, month, dayNumber));
      }
      else {
        row.push(null);
      }
    }
    weeks.push(row);
  }

  return weeks;
};

const HoverTooltip = ({ content, children }: { children: React.ReactNode; content: string }) => {
  const [open, setOpen] = useState(false);

  return (
    <PrimPopover.Root open={open} onOpenChange={setOpen}>
      <PrimPopover.Trigger
        onMouseEnter={() => { return setOpen(true); }}
        onMouseLeave={() => { return setOpen(false); }}
        className="cursor-default"
      >
        {children}
      </PrimPopover.Trigger>
      <PrimPopover.Content
        side="top"
        align="center"
        sideOffset={4}
        className="z-[100]"
        onOpenAutoFocus={(e) => { return e.preventDefault(); }}
      >
        <Text size="1" className="leading-tight">
          {content}
        </Text>
      </PrimPopover.Content>
    </PrimPopover.Root>
  );
};

interface DayCellProps {
  day: number;
  eventTypes: CalendarEventType[];
  isNextCutoff?: boolean;
  isNextShipping?: boolean;
  isPast: boolean;
  isToday: boolean;
  tooltip?: string;
}

const DayCell = ({ day, isToday, isPast, isNextShipping, isNextCutoff, eventTypes, tooltip }: DayCellProps) => {
  const cell = (
    <div className={cn(`
      flex items-center justify-center py-0.5
    `, isPast && "opacity-40")}
    >
      <div
        className={cn(
          `
            flex size-7 items-center justify-center
            leading-none
          `,
          eventTypes.length === 0 && isToday && `
            shadow-2 bg-gray-3 rounded-2
          `,
          eventTypes.includes("shipping") && (isNextShipping
            ? `
              border border-info-9 border-4 text-info-11
              rounded-full
            `
            : `
              border border-accent-9 border-4 text-accent-11
              rounded-full
            `),
          eventTypes.includes("cutoff") && (isNextCutoff
            ? `
              rounded-full border border-2 border-dashed
              border-info-8
            `
            : `
              rounded-full border border-2 border-dashed
              border-gray-8
            `),
          eventTypes.includes("holiday") && `
            line-through text-gray-11
          `,
        )}
      >
        <Text size="2">{day}</Text>
      </div>
    </div>
  );

  if (!tooltip) {
    return cell;
  }

  return (
    <HoverTooltip content={tooltip}>
      {cell}
    </HoverTooltip>
  );
};

interface LegendRowProps {
  label: string;
  type: CalendarEventType;
}

const LegendRow = ({ type, label }: LegendRowProps) => {
  return (
    <div className="flex flex-row items-center gap-2">
      <div className={`
        flex size-5 shrink-0 items-center justify-center
      `}
      >
        {type === "shipping" && (
          <div
            className="size-4 rounded-full"
            style={{ background: "conic-gradient(from 45deg, var(--accent-9) 180deg, var(--blue-9) 180deg)", padding: 3 }}
          >
            <div className="size-full rounded-full" style={{ background: "var(--color-panel-solid)" }} />
          </div>
        )}
        {type === "cutoff" && (
          <div className="relative size-4">
            <div
              className={`
                absolute inset-0 rounded-full border-2
                border-dashed border-blue-9
              `}
              style={{ clipPath: "polygon(0 0, 100% 0, 0 100%)" }}
            />
            <div
              className={`
                absolute inset-0 rounded-full border-2
                border-dashed border-gray-8
              `}
              style={{ clipPath: "polygon(100% 0, 100% 100%, 0 100%)" }}
            />
          </div>
        )}
        {type === "holiday" && (
          <Text
            size="2"
            semanticColor="gray"
            className="line-through"
          >
            15
          </Text>
        )}
      </div>
      <Text size="2" semanticColor={type === "shipping" ? "accent" : "gray"}>{label}</Text>
    </div>
  );
};

interface ShippingScheduleDialogProps {
  data: ServerDataOutput;
  now: number;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

const ShippingScheduleDialog = (props: ShippingScheduleDialogProps) => {
  const { data, now, open, onOpenChange } = props;

  const {
    subscriptionData: {
      subscriptionMeta: {
        schedule: {
          nextShippingDate: nextShippingDateString,
          nextCutoffDate: nextCutoffDateString,
          cutoffTime,
          events: scheduleEvents,
        },
      },
    },
  } = data;

  const formatter = useFormatter();
  const t = useTranslations("stf_m_subscription.screen_subscription");

  const nextShippingDate = new Date(`${nextShippingDateString}T00:00:00+07:00`);
  const nextShippingDateStr = nextShippingDate.toISOString();

  const nextCutoffDate = new Date(`${nextCutoffDateString}T00:00:00+07:00`);
  const nextCutoffDateStr = nextCutoffDate.toISOString();

  // Use the server timestamp for the hydration pass, then update from the client after mount.
  // Compute today in UTC+7.
  const today = new Date(now);
  const todayUTC7 = new Date(today.toLocaleString("en-US", { timeZone: "Asia/Bangkok" }));
  todayUTC7.setHours(0, 0, 0, 0);
  const todayStr = todayUTC7.toISOString();

  const daysUntil = getDaysUntil(nextShippingDate, now);

  const nextShippingDateFormatted = formatter.dateTime(nextShippingDate, {
    weekday: "short",
    day: "numeric",
    month: "long",
    timeZone: "Asia/Bangkok",
  });

  const nextCutoffDateFormatted = formatter.dateTime(nextCutoffDate, {
    day: "numeric",
    month: "long",
    timeZone: "Asia/Bangkok",
  });

  const cutoffTimeFormatted = cutoffTime.slice(0, 5);

  const eventMap = new Map<string, CalendarEventType[]>();
  for (const e of scheduleEvents) {
    const key = new Date(`${e.scheduleDate}T00:00:00+07:00`).toISOString();
    const types = eventMap.get(key) ?? [];
    types.push(e.type as CalendarEventType);
    eventMap.set(key, types);
  }

  const cutoffDates = scheduleEvents
    .filter((e) => { return e.type === "cutoff"; })
    .map((e) => { return new Date(`${e.scheduleDate}T00:00:00+07:00`).toISOString(); })
    .sort();

  const shippingToCutoffMap = new Map(
    scheduleEvents
      .filter((e) => { return e.type === "shipping"; })
      .map((e) => {
        const shippingDateStr = new Date(`${e.scheduleDate}T00:00:00+07:00`).toISOString();
        const cutoff = cutoffDates.filter((d) => { return d < shippingDateStr; }).pop();
        return [shippingDateStr, cutoff ? new Date(cutoff) : undefined] as const;
      })
      .filter((pair): pair is [string, Date] => { return pair[1] !== undefined; }),
  );

  const months = [0, 1, 2].map((offset) => {
    const d = new Date(todayUTC7.getFullYear(), todayUTC7.getMonth() + offset, 1);
    return { month: d.getMonth(), year: d.getFullYear() };
  });

  const todayWeekSunday = new Date(todayUTC7.getTime());
  todayWeekSunday.setDate(todayWeekSunday.getDate() - todayWeekSunday.getDay());

  return (
    <PrimDialog.Root open={open} onOpenChange={onOpenChange}>
      <PrimDialog.Content className={`
        max-w-sm gap-0 overflow-hidden p-0 rounded-4
      `}
      >
        <PrimDialog.Close />

        {/* Header */}
        <div className={`
          flex items-center px-4 pb-3 pr-10 pt-4
        `}
        >
          <PrimDialog.Title asChild>
            <Text size="4" weight="medium">
              {t("shipping_schedule_dialog.title")}
            </Text>
          </PrimDialog.Title>
        </div>

        <div className="flex flex-col gap-4 px-4 pb-4">

          {/* Next Shipment Box */}
          <div className={`
            bg-info-1 border border-info-6 rounded-3 flex
            flex-col gap-2 p-3
          `}
          >
            <div className={`
              flex flex-row items-center justify-between
            `}
            >
              <div className={`
                flex flex-row items-center gap-1.5
              `}
              >
                <TruckIcon
                  strokeWidth="1.5"
                  className={`
                    text-info-11 size-4 -scale-x-100
                  `}
                />
                <Text semanticColor="info" size="2">
                  {t("next_shipping")}
                </Text>
              </div>
              <Text semanticColor="info" size="2">
                {t("next_shipping_in_days", { days: daysUntil })}
              </Text>
            </div>
            <div className={`
              flex flex-row items-baseline justify-between
            `}
            >
              <Text semanticColor="info" size="5" weight="medium">
                {nextShippingDateFormatted}
              </Text>
              <Text semanticColor="info" size="2">
                {t("shipping_schedule_dialog.order_by", {
                  date: nextCutoffDateFormatted,
                  time: cutoffTimeFormatted,
                })}
              </Text>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-col gap-1.5">
            <LegendRow type="shipping" label={t("shipping_schedule_dialog.shipment_legend")} />
            <LegendRow type="cutoff" label={t("shipping_schedule_dialog.cutoff_legend")} />
            {/* <LegendRow type="holiday" label={t("shipping_schedule_dialog.holiday_legend")} /> */}
          </div>

          {/* Calendar */}
          <div className={`
            flex max-h-80 flex-col gap-6 overflow-y-auto
          `}
          >
            {months.map(({ year, month }, monthIndex) => {
              let weeks = getCalendarWeeks(year, month);

              if (monthIndex === 0) {
                weeks = weeks.filter((week) => {
                  const firstInWeek = week.find((d) => {
                    return d !== null;
                  });

                  if (!firstInWeek) {
                    return false;
                  }

                  const weekSunday = new Date(firstInWeek.getTime());
                  weekSunday.setDate(weekSunday.getDate() - weekSunday.getDay());
                  return weekSunday >= todayWeekSunday;
                });
              }

              const monthLabel = formatter.dateTime(new Date(year, month, 1), {
                month: "long",
                year: "numeric",
                timeZone: "Asia/Bangkok",
              });

              return (
                <div
                  key={`${year}-${month}`}
                  className="flex flex-col gap-1"
                >
                  <Text size="3" weight="medium">{monthLabel}</Text>
                  <div className={`
                    grid grid-cols-7 text-center
                  `}
                  >
                    {(["day_mon", "day_tue", "day_wed", "day_thu", "day_fri", "day_sat", "day_sun"] as const).map((key) => {
                      return (
                        <div key={key} className="py-1">
                          <Text size="1" semanticColor="gray">{t(`shipping_schedule_dialog.${key}`)}</Text>
                        </div>
                      );
                    })}
                    {weeks.flat().map((date, idx) => {
                      if (!date) {
                        return (
                          <div
                            key={`empty-${monthIndex}-${idx}`}
                            className="h-8"
                          />
                        );
                      }
                      const dateStr = date.toISOString();
                      const eventTypes = eventMap.get(dateStr) ?? [];
                      const cutoffDate = shippingToCutoffMap.get(dateStr);
                      const tooltip = eventTypes.includes("shipping") && cutoffDate
                        ? t("shipping_schedule_dialog.shipping_day_tooltip", {
                          shipDate: formatter.dateTime(date, { day: "numeric", month: "long", timeZone: "Asia/Bangkok" }),
                          cutoffDate: formatter.dateTime(cutoffDate, { day: "numeric", month: "long", timeZone: "Asia/Bangkok" }),
                          cutoffTime: cutoffTimeFormatted,
                        })
                        : undefined;
                      return (
                        <DayCell
                          key={dateStr}
                          day={date.getDate()}
                          isToday={dateStr === todayStr}
                          isPast={date < todayUTC7}
                          isNextShipping={dateStr === nextShippingDateStr}
                          isNextCutoff={dateStr === nextCutoffDateStr}
                          eventTypes={eventTypes}
                          tooltip={tooltip}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </PrimDialog.Content>
    </PrimDialog.Root>
  );
};

export { ShippingScheduleDialog };
